import { LitElement, html } from 'lit';
import styles from './studio-popup.css.mjs';

export class StudioPopup extends LitElement {
  static styles = styles;

  static properties = {
    open: { type: Boolean, reflect: true },
    title: { type: String, reflect: true },
    message: { type: String, reflect: true },
    buttonLayout: { type: String, reflect: true, attribute: 'button-layout' },
    showInput: { type: Boolean, reflect: true, attribute: 'show-input' },
    inputValue: { type: String, reflect: true, attribute: 'input-value' },
    primaryButtonText: { type: String, reflect: true, attribute: 'primary-button-text' },
    secondaryButtonText: { type: String, reflect: true, attribute: 'secondary-button-text' },
  };

  static validButtonLayouts = ['vertical', 'horizontal'];

  constructor() {
    super();
    this.open = false;
    this.title = '';
    this.message = '';
    this.buttonLayout = 'vertical';
    this.showInput = false;
    this.inputValue = '';
    this.primaryButtonText = 'Confirm';
    this.secondaryButtonText = 'Cancel';
    this._eventListeners = [];
  }

  set buttonLayout(value) {
    this._buttonLayout = StudioPopup.validButtonLayouts.includes(value) ? value : 'vertical';
  }

  get buttonLayout() {
    return this._buttonLayout;
  }

  firstUpdated() {
    if (this.open) {
      this._focusInitialElement();
    }

    const overlay = this.renderRoot.querySelector('.overlay');
    const popup = this.renderRoot.querySelector('.popup');

    this._eventListeners = [
      { element: overlay, event: 'click', handler: (e) => this._handleOverlayClick(e) },
      { element: popup, event: 'click', handler: (e) => e.stopPropagation() },
      { element: document, event: 'keydown', handler: (e) => this._handleKeydown(e) },
    ];
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('open') && this.open) {
      this._focusInitialElement();
      const overlay = this.renderRoot.querySelector('.overlay');
      const popup = this.renderRoot.querySelector('.popup');
      if (overlay && popup) {
        this._eventListeners = [
          { element: overlay, event: 'click', handler: (e) => this._handleOverlayClick(e) },
          { element: popup, event: 'click', handler: (e) => e.stopPropagation() },
          { element: document, event: 'keydown', handler: (e) => this._handleKeydown(e) },
        ];
        this._eventListeners.forEach(({ element, event, handler }) =>
          element.addEventListener(event, handler)
        );
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  _focusInitialElement() {
    const focusable = this.showInput
      ? this.renderRoot.querySelector('input')
      : this.renderRoot.querySelector('.primary');
    if (focusable) {
      focusable.focus();
    }
  }

  _handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      this.dispatchEvent(new CustomEvent('secondary-click', { bubbles: true, composed: true }));
			this.close();
    }
  }

  _handleKeydown(e) {
    if (e.key === 'Escape' && this.open) {
      e.preventDefault();
      this.close();
    }
  }

  _handlePrimaryClick() {
    this.dispatchEvent(
      new CustomEvent('primary-click', {
        detail: { inputValue: this.inputValue },
        bubbles: true,
        composed: true,
      })
    );
    this.close();
  }

  _handleSecondaryClick() {
    this.dispatchEvent(new CustomEvent('secondary-click', { bubbles: true, composed: true }));
    this.close();
  }

  _handleInputChange(e) {
    this.inputValue = e.target.value;
  }

  openPopup() {
    this.open = true;
  }

  close() {
    this.open = false;
  }

  render() {
    if (!this.open) return html``;

    return html`
      <div
        class="overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="${this.title ? 'popup-title' : ''}"
        aria-describedby="${this.message || this.showInput ? 'popup-content' : ''}"
      >
        <div class="popup">
          <div class="content" id="popup-content">
            <slot name="title">
              ${this.title ? html`<span class="title" id="popup-title">${this.title}</span>` : ''}
            </slot>
            <slot name="message">
              ${this.message ? html`<span class="message">${this.message}</span>` : ''}
            </slot>
          </div>
          ${this.showInput
            ? html`
                <div class="input-wrapper">
                  <slot name="input">
                    <input
                      type="text"
                      .value="${this.inputValue}"
                      @input="${this._handleInputChange}"
                      placeholder="Enter text"
                      aria-label="Popup input"
                    />
                  </slot>
                </div>
              `
            : ''}
          <div class="buttons ${this.buttonLayout} ${this.secondaryButtonText ? '' : 'single'}">
            <slot name="primary-button">
              <button class="primary" @click="${this._handlePrimaryClick}">
                ${this.primaryButtonText}
              </button>
            </slot>
            ${this.secondaryButtonText
              ? html`
                  <slot name="secondary-button">
                    <button class="secondary" @click="${this._handleSecondaryClick}">
                      ${this.secondaryButtonText}
                    </button>
                  </slot>
                `
              : ''}
          </div>
          <slot></slot>
        </div>
      </div>
    `;
  }
}

customElements.define('studio-popup', StudioPopup);
